import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
// Components
import Florists from 'components/orders/Florists';
import OrdersList from 'components/orders/OrdersList';
import RankedOrdersList from 'components/orders/RankedOrdersList';
import LoadingOverlay from 'components/common/LoadingOverlay';
import RestrictedSection from 'components/common/RestrictedSection';
import ViewToggle from 'components/common/ViewToggle';
import SortGroupSection from 'components/orders/SortGroupSection';
import BySku from 'components/groupedBySku/BySku';
import ByZone from 'components/groupedByZone/ByZone';
// Helpers
import {
  convertQueryParamsIntoObject,
  convertObjectIntoQueryParams,
  formatDateTime,
} from 'constants/Helpers';
import { useOnScreen } from 'components/common/hooks/useOnScreen';
// Selectors
import { getIsLoaderOpen } from 'store/app/AppSelectors';
import {
  getDeliveryData,
  getorderDetailsData,
  getPageCount,
  getGroupBySkuData,
  getGroupByZoneData,
} from 'store/orders/OrdersListSelectors';
// API
import OrdersListService from 'services/OrdersListService';
// Actions
import { setLoaderOpenTrue, setLoaderOpenFalse } from 'store/app/AppActions';
import { getFloristsData } from 'store/orders/OrdersListActions';
// urls
import { getOrdersPageUrl, getMyTasksPageUrl } from 'constants/AppUrls';

// The grouped-by endpoints are still slot scoped - 0 asks for every slot.
const ALL_SLOTS = 0;

const HFOrdersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const ref = useRef();
  const ordersListData = useSelector((state) => getorderDetailsData({ state }));
  const deliveryDate = useSelector((state) => getDeliveryData({ state }));
  const pageCount = useSelector((state) => getPageCount({ state }));
  const [counter, setCounter] = useState(1);
  const isLoaderOpen = useSelector((state) => getIsLoaderOpen({ state }));
  const queryObj = location.search ? convertQueryParamsIntoObject(location.search) : {};
  const isExpress = queryObj.express === 'true';
  const onScreen = useOnScreen(ref, '200px');
  const groupBySkuData = useSelector((state) => getGroupBySkuData({ state }));
  const groupByZoneData = useSelector((state) => getGroupByZoneData({ state }));

  const [initialParams, setInitialParams] = useState({
    page: 1,
    deliveryDate: formatDateTime(deliveryDate),
    floristId: -1,
    sortBy: 6,
    FloristOrdersGroupBy: -1,
  });
  const [currentView, setCurrentView] = useState(2);

  const fetchMoreData = async (params, page, isSortBy = false, silent = false) => {
    let clonedParams = { ...params, page: counter };
    if (page) {
      setCounter(1);
      clonedParams = { ...params, page: 1 };
    }
    if (page || counter <= pageCount || isSortBy) {
      if (!silent) dispatch(setLoaderOpenTrue());
      const isExpressMode = clonedParams.express === 'true';
      try {
        if (isExpressMode) {
          const resDef = await OrdersListService.getExpressOrdersListing(clonedParams);
          dispatch({
            type: 'SET_ORDERS_LIST_DATA',
            payload: {
              ordersList: resDef?.data?.data,
              pageCount: resDef?.data?.pageCount,
              // Only a page reset replaces the list - scrolling appends to it.
              isFlorist: page ? true : false,
            },
          });
        } else {
          switch (parseInt(params.FloristOrdersGroupBy, 10)) {
            case -1:
              const resDef = await OrdersListService.getordersListData(clonedParams);
              dispatch({
                type: 'SET_ORDERS_LIST_DATA',
                payload: {
                  ordersList: resDef?.data?.data,
                  pageCount: resDef?.data?.pageCount,
                  isFlorist: page ? true : false,
                },
              });
              break;
            case 0:
              const resSKU = await OrdersListService.getGroupedBySku(clonedParams, ALL_SLOTS);
              if (page) {
                dispatch({
                  type: 'SET_GROUP_BY_SKU_DATA',
                  payload: resSKU?.data,
                });
              } else {
                dispatch({
                  type: 'SET_GROUP_BY_SKU_APPENDED_DATA',
                  payload: resSKU?.data,
                });
              }
              break;
            case 1:
              const resZone = await OrdersListService.getGroupedByZone(clonedParams, ALL_SLOTS);
              if (page) {
                dispatch({
                  type: 'SET_GROUP_BY_ZONE_DATA',
                  payload: resZone?.data,
                });
              } else {
                dispatch({
                  type: 'SET_GROUP_BY_ZONE_APPENDED_DATA',
                  payload: resZone?.data,
                });
              }
              break;

            default:
              break;
          }
        }
        if (!isExpressMode && !silent) {
          navigate({
            pathname: '/orders',
            search: convertObjectIntoQueryParams(clonedParams),
          }, { replace: true });
        }
        setCounter((prev) => prev + 1);
        if (!silent) dispatch(setLoaderOpenFalse());
      } catch (err) {
        if (!silent) dispatch(setLoaderOpenFalse());
      }
    }
  };

  const fetchData = (page, silent = false) => {
    setInitialParams(queryObj);
    return fetchMoreData(queryObj, page, false, silent);
  };

  const onViewChange = (vIndx, vURL) => {
    setCurrentView(vIndx);
    navigate({
      pathname: vURL,
      search: convertObjectIntoQueryParams({
        sortBy: queryObj?.sortBy || 6,
        deliveryDate: queryObj?.deliveryDate,
        searchKey: queryObj?.searchKey || '',
        FloristOrdersGroupBy: queryObj?.FloristOrdersGroupBy || -1,
      }),
    });
  };

  const fetchFlorists = async () => {
    dispatch(
      getFloristsData({
        deliveryDate: queryObj.deliveryDate,
      })
    );
  };

  const handleGroupReturnedComponent = (groupBy) => {
    switch (parseInt(groupBy, 10)) {
      case -1:
        return (
          <OrdersList
            ordersListData={ordersListData}
            fetchMoreData={fetchData}
            fetchFlorists={fetchFlorists}
            initialParams={initialParams}
            onScreen={onScreen}
          />
        );
      case 0:
        return (
          <BySku
            navigate={navigate}
            fetchMoreData={fetchData}
            initialParams={initialParams}
            groupBySkuData={groupBySkuData}
            onScreen={onScreen}
            isLoaderOpen={isLoaderOpen}
          />
        );
      case 1:
        return (
          <ByZone
            navigate={navigate}
            fetchMoreData={fetchData}
            initialParams={initialParams}
            groupByZoneData={groupByZoneData}
            onScreen={onScreen}
            isLoaderOpen={isLoaderOpen}
          />
        );

      default:
        break;
    }
  };

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryObj.FloristOrdersGroupBy, queryObj.express]);

  // Auto-refresh the orders list every 5 minutes to keep ranks up to date.
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchData(1, true);
    }, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <>
      {isLoaderOpen && <LoadingOverlay />}
      {!isExpress && (
        <>
          <SortGroupSection
            fetchData={fetchMoreData}
            setInitialParams={setInitialParams}
            baseURL={getOrdersPageUrl()}
          />
          <ViewToggle activeView={currentView} onViewChange={onViewChange}>
            <div label="My Tasks" url={getMyTasksPageUrl()} viewIndex={1} />
            <div label="All Orders" url={getOrdersPageUrl()} viewIndex={2} />
          </ViewToggle>
          <RestrictedSection action="view_florists" page="florist_orders">
            <Florists
              initialParams={initialParams}
              setCounter={setCounter}
              fetchFlorists={fetchFlorists}
            />
          </RestrictedSection>
        </>
      )}
      {isExpress ? (
        // Express keeps its own page, rendered with the orders list card design.
        <div className="ranked-orders-page">
          <RankedOrdersList
            ordersListData={ordersListData}
            fetchMoreData={fetchData}
            initialParams={initialParams}
            onScreen={onScreen}
            isLoading={isLoaderOpen}
            loadMoreOnScreen
            emptyText="No express orders"
          />
        </div>
      ) : (
        handleGroupReturnedComponent(initialParams.FloristOrdersGroupBy)
      )}
      <div ref={ref} />
    </>
  );
};

export default HFOrdersPage;
